import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { Input } from '@payscale/design';
import { CycleBudget } from 'nomst/Stores/Configuration/cycleBudget';
import EditBudgetRow from 'components/BudgetConfiguration/editBudgetRow';
import SyncWithLabDialog from 'components/BudgetConfiguration/syncWithLabDialog';
import Loading from 'components/Common/loading';
import { ManagersCountryBudget } from 'nomst/Stores/Budget/countryBudget';
import { Select, Option, FormGroup } from '@payscale/design';
import { EventHelper } from 'common/eventHelper';
import { budgetConfigurationViewStore, editBudgetViewStore,
    currencyConversion, configurationsStore } from 'utils/MobxDataManager/singletons';
import { navigateToNoMst } from 'utils/MobxDataManager/singletons';
import './editBudgetView.scss';
import { BudgetGroupingOptions } from 'typings/enums';
import { ManagerBudget } from 'nomst/Stores/Budget/managerBudget';
import GroupBudgetView from 'components/BudgetConfiguration/groupedBudgetView';

export interface IEditBudgetViewProps {
    budget: CycleBudget;
    readonly?: boolean;
}

export class EditBudgetView extends React.Component<IEditBudgetViewProps> {
    constructor(props: IEditBudgetViewProps) {
        super(props);
        // tslint:disable-next-line:no-floating-promises
        this.populateViewData();
        this.handleChangePercentage = this.handleChangePercentage.bind(this);
        this.handleBlurPercentage = this.handleBlurPercentage.bind(this);
        this.handleChangeValue = this.handleChangeValue.bind(this);
        this.handleBlurValue = this.handleBlurValue.bind(this);
        this.handleChangeUseProration = this.handleChangeUseProration.bind(this);
        this.handleResetView = this.handleResetView.bind(this);
        this.handleOpenEditBudget = this.handleOpenEditBudget.bind(this);
        this.handleExportCountryBudget = this.handleExportCountryBudget.bind(this);
        this.handleClickApplyBudgets = this.handleClickApplyBudgets.bind(this);
        this.handleCountryChange = this.handleCountryChange.bind(this);
        this.handleCurrencyChange = this.handleCurrencyChange.bind(this);
        this.handleKeyPressPercentage = this.handleKeyPressPercentage.bind(this);
        this.handleKeyPressValue = this.handleKeyPressValue.bind(this);
        this.handleSetSyncWithLab = this.handleSetSyncWithLab.bind(this);
        this.handleConfirmSetSyncWithLab = this.handleConfirmSetSyncWithLab.bind(this);
        this.hideSyncWithLabDialog = this.hideSyncWithLabDialog.bind(this);
        this.renderManagerBudgets = this.renderManagerBudgets.bind(this);
    }

    componentDidMount(): void {
        const { budget } = this.props;
        currencyConversion.resetCurrencySelection();
        editBudgetViewStore.setInitialUseProration(budget.useProration);
        editBudgetViewStore.setCurrentBudget(budget);
    }

    componentWillUnmount(): void {
        editBudgetViewStore.resetView();
    }

    render(): React.ReactNode {
        if (!configurationsStore.convertedCycleSettingsViewCycle) {
            return null;
        }

        const { multiCountry } = configurationsStore.convertedCycleSettingsViewCycle.teamPageViews;
        const { budget, readonly } = this.props;
        return (
            <div className="edit-budget">
                <div className="page-header flex-row">
                    <div>
                        <button onClick={this.handleResetView} className="go-back-link pxl-link">
                            <i className="icon-reply" />
                            <span>Return to budgets</span>
                        </button>
                        <h1 className="title">
                            {budget.name}
                            <button
                                className="pxl-link"
                                hidden={readonly}
                                onClick={this.handleOpenEditBudget}
                            >
                                <i className="icon-cog" />
                            </button>
                        </h1>
                        <p className="description">for {this.props.budget.associatedFieldsText}</p>
                    </div>
                    <div>
                        <button
                            className="export-button pxl-link"
                            onClick={this.handleExportCountryBudget}
                        >
                            <i className="icon-download" />
                            <span>Export</span>
                        </button>
                        <button
                            className="pxl-btn apply-budget-button"
                            hidden={budget.autoSync}
                            disabled={editBudgetViewStore.shouldDisableInput(budget)}
                            onClick={this.handleClickApplyBudgets}
                        >
                            Apply Budget
                        </button>
                    </div>
                </div>
                <div>
                    <div className="flex-row budget-options">
                        <div className="flex-row">
                            <div
                                className="pxl-form-group first-option"
                                hidden={!multiCountry}
                            >
                                <label htmlFor="selectCountry">Country</label>
                                <Select
                                    id="selectCountry"
                                    value={editBudgetViewStore.selectedCountryCode}
                                    onChange={this.handleCountryChange}
                                    options={editBudgetViewStore.availableCountryOptions}
                                />
                            </div>
                            <div
                                className="pxl-form-group second-option"
                                hidden={!editBudgetViewStore.showGroupByOption()}
                            >
                                <label htmlFor="selectGroupBy">Group By</label>
                                <Select
                                    id="selectGroupBy"
                                    value={editBudgetViewStore.selectedGroupByOption}
                                    onChange={this.handleGroupByChange}
                                    options={editBudgetViewStore.availableGroupByOptions}
                                />
                            </div>
                            <div
                                className="pxl-form-group"
                                hidden={!editBudgetViewStore.shouldShowMultiCurrencyDropdown(budget)}
                            >
                                <label htmlFor="selectCurrency">Currency</label>
                                <Select
                                    id="selectCurrency"
                                    value={currencyConversion.selectedCurrency}
                                    onChange={this.handleCurrencyChange}
                                    options={currencyConversion.availableCurrencyOptions}
                                />
                            </div>
                        </div>
                        <div className="flex-row">
                            <div
                                hidden={budget.autoSync}
                                className="pxl-form-group"
                            >
                                <label>
                                    {editBudgetViewStore.targetBudgetLabelText(budget)}
                                </label>
                                {this.renderSetBudgetAllManagerInput()}
                            </div>
                            <div
                                id="insightLabSync"
                                className="pxl-form-group last-option"
                                hidden={!budget.hasAnyLabAmount}
                            >
                                <label htmlFor="selectLabSync">Insight Lab Sync</label>
                                <Select
                                    id="selectLabSync"
                                    value={budget.autoSync}
                                    disabled={readonly}
                                    onChange={this.handleSetSyncWithLab}
                                    options={[
                                        { text: 'On', value: true },
                                        { text: 'Off', value: false }
                                    ]}
                                />
                            </div>
                        </div>
                    </div>
                    <div
                        hidden={!budget.hasAnyLabAmount || !budget.autoSync}
                        className="flex-row last-sync"
                    >
                        <p>
                            Last Sync {budget.lastSyncDateText}
                        </p>
                    </div>
                    {this.renderProrationCheckbox()}
                </div>
                <table className="budget-table">
                    <thead>
                        <tr>
                            <th>Manager</th>
                            <th hidden={!editBudgetViewStore.shouldShowTotalCurrentPay(budget)}>
                                Total Current Pay
                            </th>
                            <th hidden={editBudgetViewStore.shouldShowOriginalBudget(budget) === false}>
                                Original Budget
                            </th>
                            <th hidden={!editBudgetViewStore.useProration}>
                                Budget Before Proration
                            </th>
                            <th hidden={!editBudgetViewStore.useProration}>Prorated Budget</th>
                            <th hidden={!editBudgetViewStore.shouldShowAdjustedTargetPercentage(budget)}>
                                Adjusted Target %
                            </th>
                            <th>Adjusted Budget</th>
                            <th>Spent</th>
                            <th>Balance</th>
                        </tr>
                    </thead>
                    {this.renderManagers()}
                </table>
                {editBudgetViewStore.isLoading &&
                    <div className="loading-message">
                        <h2>We are processing the budget of your organization. Please do not refresh your page or navigate away.</h2>
                        <Loading />
                    </div>
                }
                {this.renderSyncWithLabDialog()}
            </div>
        );
    }

    private handleResetView(): void {
        budgetConfigurationViewStore.resetView();
        navigateToNoMst.navigateToMstBudgetSettings(configurationsStore.convertedCycleSettingsViewCycle.cycleId);
    }

    private handleOpenEditBudget(): void {
        const { budget } = this.props;
        budgetConfigurationViewStore.openEditBudgetModal(
            budget,
            (budgetToEdit: CycleBudget) => editBudgetViewStore
                .fetchBudgetSummaryByCountry(editBudgetViewStore.selectedCountryCode, budgetToEdit));
    }

    private handleExportCountryBudget(): Promise<void> {
        const { budget } = this.props;
        return editBudgetViewStore.exportCountryBudgetToCsv(budget.budgetCycleId, budget.name);
    }

    private handleClickApplyBudgets(): Promise<void> {
        const { budget } = this.props;
        return editBudgetViewStore.applyBudgets(budget);
    }

    private handleCountryChange(_: undefined, value: string): Promise<void> {
        editBudgetViewStore.changeEmployeeCountrySummary(value);
        return editBudgetViewStore.fetchBudgetSummaryByCountry(
            editBudgetViewStore.selectedCountryCode,
            this.props.budget);
    }

    private handleCurrencyChange(_: undefined, value: string): void {
        currencyConversion.setCurrency(value);
    }

    private handleGroupByChange(_: undefined, value: string): void {
        editBudgetViewStore.setSelectGroupByOption(value);
    }

    private async populateViewData(): Promise<void> {
        if (!configurationsStore.convertedCycleSettingsViewCycle) {
            return;
        }
        const { multiCountry } = configurationsStore.convertedCycleSettingsViewCycle.teamPageViews;
        if (!multiCountry) {
            await editBudgetViewStore.fetchBudgetSummaryAllCountries(this.props.budget);
            return;
        }

        await editBudgetViewStore.fetchEmployeeCountrySummaries(this.props.budget);
        await editBudgetViewStore.fetchBudgetSummaryByCountry(
            editBudgetViewStore.selectedCountryCode,
            this.props.budget);
    }

    private renderProrationCheckbox(): React.ReactNode {
        const { budget } = this.props;
        if (!editBudgetViewStore.checkIfBudgetHasValidProration(budget) || budget.autoSync) {
            return null;
        }

        return (
            <div className="flex-row pxl-form-group">
                <Option
                    id="ProrationCheckbox"
                    onChange={this.handleChangeUseProration}
                    checked={editBudgetViewStore.useProration}
                    text="Enable Proration"
                    className="pxl-option--secondary"
                />
            </div>
        );
    }

    private handleChangeUseProration(_: undefined, event: IOnChangeEvent): void {
        editBudgetViewStore.toggleUseProration(event.target.checked);
    }

    private renderManagers(): React.ReactNode {
        if (editBudgetViewStore.isLoading === true) {
            return null;
        }

        const countryBudget = editBudgetViewStore.countryBudget;
        if (!countryBudget) {
            return null;
        }

        const { selectedGroupByOption } = editBudgetViewStore;

        
        switch(selectedGroupByOption) {
            case BudgetGroupingOptions.Department:
            case BudgetGroupingOptions.WorkLocation:
            case BudgetGroupingOptions.BusinessUnit:
                return this.renderGroupView(countryBudget);
            case BudgetGroupingOptions.All:
            default:
                return (
                    <tbody>
                        {this.renderCountryBudget(countryBudget)}
                        {this.renderManagerBudgets(countryBudget.managers)}
                    </tbody>
                );
        }
        
    }

    private renderGroupView(countryBudget: ManagersCountryBudget): React.ReactNode {
        return (
            <>
                <tbody>
                    {this.renderCountryBudget(countryBudget, 'country-budget--grouped')}
                </tbody>
                {this.renderGroupedBudgets()}
            </>
        );
    }

    private renderGroupedBudgets(): React.ReactNode {
        const { groupedSummaryBudgets, currentConfiguration } = editBudgetViewStore;
        const { budget } = this.props;
        return groupedSummaryBudgets.map((groupedManagerBudget, index) => {
            return (
                <GroupBudgetView
                    key={index}
                    groupedSummaryBudget={groupedManagerBudget}
                    budget={budget}
                    useAutoSync={budget.autoSync}
                    configuration={currentConfiguration}
                    renderManagerBudgets={this.renderManagerBudgets}/>
            );
        });
    }

    private renderCountryBudget(countryBudget: ManagersCountryBudget, classNameModifier?: string): React.ReactNode {
        const { budget } = this.props;
        const className = classNameModifier ? `country-budget ${classNameModifier}` : 'country-budget';
        return (
            <tr className={className}>
                <td>All Managers</td>
                <td hidden={!editBudgetViewStore.shouldShowTotalCurrentPay(budget)}>
                    {countryBudget.totalCurrentPayText}
                </td>
                <td hidden={editBudgetViewStore.shouldShowOriginalBudget(budget) === false}>
                    {countryBudget.originalBudgetText}
                </td>
                <td hidden={!editBudgetViewStore.useProration}>{countryBudget.adjustedBudgetBeforeProrationText}</td>
                <th hidden={!editBudgetViewStore.useProration}>{countryBudget.proratedBudgetText}</th>
                <td hidden={!editBudgetViewStore.shouldShowAdjustedTargetPercentage(budget)}>
                    {countryBudget.adjustedBudgetPercentageText}
                </td>
                <td>{countryBudget.adjustedBudgetText}</td>
                <td>{countryBudget.totalSpentText}</td>
                <td className={`${countryBudget.budgetBalanceClass}`}>{countryBudget.budgetBalanceText}</td>
            </tr>
        );
    }

    private renderManagerBudgets(
        managers: ManagerBudget[],
        turnOffTargetBudgetPercentageUpdateFlag?: () => void,
        turnOffTargetBudgetValueUpdateFlag?: () => void): React.ReactNode {
        const { budget } = this.props;

        return managers.map((managerBudget) => {
            return (
                <EditBudgetRow
                    key={managerBudget.employeeSourceId}
                    showOriginalBudget={editBudgetViewStore.shouldShowOriginalBudget(budget)}
                    showTotalCurrentPay={editBudgetViewStore.shouldShowTotalCurrentPay(budget)}
                    showAdjustedTargetPercentage={editBudgetViewStore.shouldShowAdjustedTargetPercentage(budget)}
                    useAutoSync={budget.autoSync}
                    useProration={editBudgetViewStore.useProration}
                    managerBudget={managerBudget}
                    configuration={editBudgetViewStore.currentConfiguration}
                    onManagerBudgetPercentChangeCallbk={turnOffTargetBudgetPercentageUpdateFlag}
                    onManagerBudgetValueChangeCallbk={turnOffTargetBudgetValueUpdateFlag}
                />
            );
        });
    }

    private renderSetBudgetAllManagerInput(): React.ReactNode {
        if (this.props.budget.isStockBudgetInUnitOnly) {
            return (
                <Input
                    value={editBudgetViewStore.budgetValue.getValueText()}
                    id="increaseByValueAllManager"
                    disabled={editBudgetViewStore.shouldDisableInput(this.props.budget)}
                    onChange={this.handleChangeValue}
                    onBlur={this.handleBlurValue}
                    onKeyPress={this.handleKeyPressValue}
                />
            );
        }

        return (
            <FormGroup error={editBudgetViewStore.budgetPercentage.errorMessageText} >
                <Input
                    value={editBudgetViewStore.budgetPercentage.increasePercentageText}
                    id="increaseByPercentageAllManager"
                    disabled={editBudgetViewStore.shouldDisableInput(this.props.budget)}
                    onChange={this.handleChangePercentage}
                    onBlur={this.handleBlurPercentage}
                    onKeyPress={this.handleKeyPressPercentage}
                />
            </FormGroup>
        );
    }

    private renderSyncWithLabDialog(): React.ReactNode {
        if (!editBudgetViewStore.shouldShowSyncWithLabDialog) {
            return null;
        }

        return (
            <SyncWithLabDialog
                unconfirmedUseAutoSync={editBudgetViewStore.unconfirmedUseAutoSync}
                onClose={this.hideSyncWithLabDialog}
                onConfirm={this.handleConfirmSetSyncWithLab}
            />
        );
    }

    private hideSyncWithLabDialog(): void {
        editBudgetViewStore.hideSyncWithLabDialog();
    }

    private handleKeyPressPercentage(event: React.KeyboardEvent<HTMLInputElement>): void {
        if (!EventHelper.isKeyEnterEvent(event)) {
            return;
        }

        editBudgetViewStore.budgetPercentage.onBlur();
    }

    private handleKeyPressValue(event: React.KeyboardEvent<HTMLInputElement>): void {
        if (!EventHelper.isKeyEnterEvent(event)) {
            return;
        }

        editBudgetViewStore.budgetValue.onBlur();
    }

    private handleChangePercentage(_: undefined, value: string): void {
        editBudgetViewStore.budgetPercentage.changePercentage(value);
    }

    private handleBlurPercentage(): void {
        editBudgetViewStore.budgetPercentage.onBlur();
    }

    private handleChangeValue(_: undefined, value: string): void {
        editBudgetViewStore.budgetValue.changeValue(value);
    }

    private handleBlurValue(): void {
        editBudgetViewStore.budgetValue.onBlur();
    }

    private handleSetSyncWithLab(_: undefined, boolValueText: string): void {
        const boolValue = boolValueText === 'true';
        editBudgetViewStore.handleSetSyncWithLab(boolValue);
    }

    private async handleConfirmSetSyncWithLab(useAutoSync: boolean): Promise<void> {
        const { budget } = this.props;
        budget.setAutoSyncWithLab(useAutoSync);
        await editBudgetViewStore.setBudgetAutoSyncAsync(budget);

        if (useAutoSync) {
            budget.setLastSyncDate();
            editBudgetViewStore.resetView();
            await this.populateViewData();
        }
    }
}

export default inject('store')(observer(EditBudgetView));
